import './App.css';
import React, { useEffect, useState } from 'react';
import { Route, Switch } from 'react-router-dom';
import { Dashboard } from './pages/dashboard/Dashboard';
import { Styleguide } from './pages/Styleguide/Styleguide';
import { Sidebar } from './components/Sidebar/Sidebar';
import { Navbar } from './components/Navbar/Navbar';
import { DBInspector } from './components/DBInspector/DBInspector';
import { useData } from './model/data';
import { Login } from './pages/Login/Login';
import { Queue } from './pages/Queue/Queue';
import { ForgotPassword } from './pages/Login/ForgotPassword';
import { CompanyDetails } from './pages/Companies/CompanyDetails/CompanyDetails';
import { Companies } from './pages/Companies/Companies';
import { Tours } from './pages/ticketing/Tours/Tours';
import { TourDetails } from './pages/ticketing/Tours/TourDetails';
import { Events } from './pages/ticketing/Events/Events';
import { Users } from './pages/Users/Users';
import { UserDetails } from './pages/Users/UserDetails/UserDetails';
import { Venues } from './pages/ticketing/Venues/Venues';
import { Event } from './pages/ticketing/Events/Event/Event';
import { VenueDetails } from './pages/ticketing/Venues/VenueDetails';
import { Products } from './pages/Products/Products';
import { ProductDetails } from './pages/Products/Product/ProductDetails';
import {
  tourPath,
  ordersPath,
  orderPath,
  resetPasswordPath,
  invoicePath,
  picklistPath,
  picklistsPath,
  deliveryPath,
  fulfillmentPath,
  fulfillmentDetailsPath,
  queuePath,
  shopsPath,
  shopPath,
  newsPath,
  newsDetailsPath,
  contractPath,
  contractsPath,
  merchStatisticsPath,
  scannerPath,
  signUpPath,
  invitesPath,
  venueLayoutPath,
  chartsPath,
  eventTurnoversPath,
  voucherCampaignsPath,
  voucherCampaignPath,
} from './routes';
import {
  dashboardPath,
  styleguidePath,
  companiesPath,
  eventsPath,
  eventPath,
  companyPath,
  venuesPath,
  toursPath,
  productsPath,
  venuePath,
  usersPath,
  userPath,
  productPath,
} from './routes';
import { Orders } from './pages/ticketing/Orders/Orders';
import { OrderDetails } from './pages/ticketing/Orders/OrderDetails/OrderDetails';
import { Shops } from './pages/Products/Shops/Shops';
import { Notifications } from './components/Notifications/Notifications';
import { Invoice } from './pages/ticketing/Orders/Invoice/Invoice';
import { Picklists } from './pages/Picklists/Picklists';
import { PicklistDetails } from './pages/Picklists/PicklistDetails';
import ErrorBoundary from './pages/ErrorBoundary/ErrorBoundary';
import { Delivery } from './pages/ticketing/Orders/Delivery/Delivery';
import Fulfillment from './pages/Fulfillment/Fulfillment';
import { FulfillmentDetails } from './pages/Fulfillment/FulfillmentDetails/FulfillmentDetails';
import { destroySession, refreshSession } from './model/session';
import { categoriesPath, categoryPath, settingsPath, bookKeepingExportPath } from './routes';
import { Categories } from './pages/Categories/Categories';
import { CategoryDetails } from './pages/Categories/CategoryDetails';
import { ShopDetails } from './pages/Products/Shops/ShopDetails';
import { News } from './pages/News/News';
import { NewsItem } from './pages/News/news_item';
import { Contracts } from './pages/Contracts/Contracts';
import { ContractDetails } from './pages/Contracts/ContractDetails/ContractDetails';
import { BookKeepingExport } from './pages/BookKeeping/BookKeepingExport';
import { ServerSettings } from './pages/ServerSettings/ServerSettings';
import { backPaymentsPath } from './routes';
import { BackPayments } from './pages/ticketing/Orders/BackPayments';
import { TicketScanner } from './pages/TicketScanner/TicketScanner';
import { useLocation } from 'react-router-dom';
import { ConfirmationPrompt, ConfirmationWrapper } from './components/ConfirmationPrompt/ConfirmationPrompt';
import { BuildCheck } from './components/BuildCheck/BuildCheck';
import { t } from './locales/translate';
import { LoadingPage } from './components/LoadingPage/LoadingPage';
import { MerchStatistics } from './pages/merchandise/MerchStatistics/MerchStatistics';
import { Signup } from './pages/Login/Signup';
import { Invites } from './pages/Invites/Invites';
import { ErrorPage } from './pages/ErrorPage/ErrorPage';
import { Button } from './components/Button/Button';
import { writeDB } from './model/store';
import { VenueLayoutDetails } from './pages/ticketing/VenueLayouts/VenueLayoutDetails';
import { Charts } from './pages/charts/charts';
import { Turnover } from './pages/Turnover/Turnover';
import { VoucherCampaigns } from './pages/voucher_campaigns';
import { VoucherCampaign } from './pages/voucher_campaigns/voucher_campaign';

function ScrollToTop(): null {
  const { pathname } = useLocation();

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [pathname]);

  return null;
}

export default function App(): JSX.Element {
  const session = useData((db) => db.get('session'));
  const loggedIn = !!session;

  const [refreshingSession, setRefreshingSession] = useState(true);
  useEffect(() => {
    (async () => {
      try {
        await refreshSession();
      } finally {
        setRefreshingSession(false);
      }
    })();
  }, []);

  useEffect(() => {
    const selectedMembership = writeDB.get('selectedMembership');

    if (!session?.membershipIds.length) {
      writeDB.set('selectedMembership', undefined);
    } else if (!selectedMembership) {
      writeDB.set('selectedMembership', session.membershipIds[0]);
    }
  }, [session?.membershipIds.length]);

  if (refreshingSession) {
    return <LoadingPage title={t.actions.loading('')} />;
  }

  return (
    <Switch>
      <Route exact path={scannerPath()} component={TicketScanner} />
      <Route path="/">
        {() => (
          <div className="app">
            <ScrollToTop />
            <Notifications />
            <BuildCheck />
            <ConfirmationWrapper>
              <ConfirmationPrompt />
              {!loggedIn && (
                <Switch>
                  <Route exact path={resetPasswordPath()} component={ForgotPassword} />
                  <Route path={signUpPath(':token')} component={Signup} />
                  <Route path="/" component={Login} />
                </Switch>
              )}
              {loggedIn && (
                <Switch>
                  <Route exact path={invitesPath(':token')} component={Invites} />
                  {!session.membershipIds.length ? (
                    <Route
                      exact
                      path="*"
                      component={() => (
                        <ErrorPage
                          error={new Error(t.model.membership.noLongerAMember)}
                          action={<Button type="primary" title="Logout" block onClick={() => destroySession()} />}
                        />
                      )}
                    />
                  ) : (
                    <>
                      <div className="app__sidebar">
                        <Sidebar />
                      </div>
                      <div className="app__container">
                        <div className="app__navbar">
                          <Navbar />
                        </div>
                        <div className="app__main">
                          <DBInspector />
                          <ErrorBoundary>
                            <Switch>
                              {/* TICKET SCANNER */}
                              <Route exact path={scannerPath()} component={TicketScanner} />
                              {/* DASHBOARD */}
                              <Route exact path={dashboardPath()} component={Dashboard} />
                              {/* EVENTS */}
                              <Route exact path={eventsPath()} component={Events} />
                              <Route exact path={eventPath(':id')} component={Event} />
                              <Route exact path={eventPath(':id/:section')} component={Event} />
                              <Route exact path={eventPath(':id/:section/:subsection')} component={Event} />
                              {/* TOURS */}
                              <Route exact path={toursPath()} component={Tours} />
                              <Route exact path={tourPath(':id')} component={TourDetails} />
                              {/* VENUES */}
                              <Route exact path={venuesPath()} component={Venues} />
                              <Route exact path={venuePath(':id')} component={VenueDetails} />
                              {/* VENUE LAYOUTS */}
                              <Route exact path={venueLayoutPath(':id')} component={VenueLayoutDetails} />
                              {/* TURNOVER */}
                              <Route exact path={eventTurnoversPath()} component={Turnover} />
                              {/* PRODUCTS */}
                              <Route exact path={productsPath()} component={Products} />
                              <Route exact path={productPath(':id')} component={ProductDetails} />
                              {/* CATEGORIES */}
                              <Route exact path={categoriesPath()} component={Categories} />
                              <Route exact path={categoryPath(':id')} component={CategoryDetails} />
                              {/* MERCH STATISTICS */}
                              <Route exact path={merchStatisticsPath()} component={MerchStatistics} />
                              {/* SHOPS */}
                              <Route exact path={shopsPath()} component={Shops} />
                              <Route exact path={shopPath(':id')} component={ShopDetails} />
                              {/* CONTENT & CAMPAIGNS */}
                              {/* NEWS */}
                              <Route exact path={newsPath()} component={News} />
                              <Route exact path={newsDetailsPath(':id')} component={NewsItem} />
                              {/* VOUCHER CAMPAIGNS */}
                              <Route exact path={voucherCampaignsPath()} component={VoucherCampaigns} />
                              <Route exact path={voucherCampaignPath(':id')} component={VoucherCampaign} />
                              {/* ORDERS */}
                              <Route exact path={ordersPath()} component={Orders} />
                              <Route exact path={orderPath(':id')} component={OrderDetails} />
                              <Route exact path={invoicePath(':orderId', ':invoiceId')} component={Invoice} />
                              <Route exact path={deliveryPath(':orderId', ':deliveryId')} component={Delivery} />
                              {/* PICKLISTS */}
                              <Route exact path={picklistsPath()} component={Picklists} />
                              <Route exact path={picklistPath(':id')} component={PicklistDetails} />
                              {/* FULFILLMENT */}
                              <Route exact path={fulfillmentPath()} component={Fulfillment} />
                              <Route exact path={fulfillmentDetailsPath(':id')} component={FulfillmentDetails} />
                              <Route exact path={backPaymentsPath()} component={BackPayments} />
                              {/* STYLEGUIDE */}
                              <Route exact path={styleguidePath()} component={Styleguide} />
                              {/* WAITING QUEUE */}
                              <Route exact path={queuePath()} component={Queue} />
                              {/* CONTRACTS */}
                              <Route exact path={contractsPath()} component={Contracts} />
                              <Route exact path={contractPath(':id')} component={ContractDetails} />
                              {/* COMPANIES */}
                              <Route exact path={companiesPath()} component={Companies} />
                              <Route exact path={companyPath(':id')} component={CompanyDetails} />
                              {/* USERS */}
                              <Route exact path={usersPath()} component={Users} />
                              <Route exact path={userPath(':id')} component={UserDetails} />
                              {/* BookKeeping Export */}
                              <Route exact path={bookKeepingExportPath()} component={BookKeepingExport} />
                              {/* Settings */}
                              <Route exact path={settingsPath()} component={ServerSettings} />
                              {/* CHARTS */}
                              <Route exact path={chartsPath()} component={Charts} />
                              {/* NOT FOUND */}
                              <Route
                                path="*"
                                component={() => <ErrorPage error={new Error('Page not found')} simple />}
                              />
                            </Switch>
                          </ErrorBoundary>
                        </div>
                      </div>
                    </>
                  )}
                </Switch>
              )}
            </ConfirmationWrapper>
          </div>
        )}
      </Route>
    </Switch>
  );
}
